import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

/**
 * Always-skip entries: build/dep dirs, lockfiles, binaries, and — critically —
 * secret-bearing files (.env*, keys, certs) so they are never read, chunked, or
 * embedded (the first line of the §16 redaction defense).
 */
const DEFAULT_IGNORES = [
  '.git',
  'node_modules',
  'dist',
  'build',
  '.next',
  '.turbo',
  'coverage',
  '.data',
  '.cache',
  'pnpm-lock.yaml',
  'yarn.lock',
  'package-lock.json',
  '*.lock',
  '.env',
  '.env.*',
  '*.pem',
  '*.key',
  '*.crt',
  '*.p12',
  '*.pfx',
  // binaries / assets
  '*.png',
  '*.jpg',
  '*.jpeg',
  '*.gif',
  '*.ico',
  '*.webp',
  '*.svg',
  '*.pdf',
  '*.zip',
  '*.gz',
  '*.tar',
  '*.wasm',
  '*.node',
  '*.dll',
  '*.exe',
  '*.woff',
  '*.woff2',
  '*.ttf',
  '*.mp4',
  '*.mp3',
];

interface CompiledPattern {
  re: RegExp;
  /** Match against the basename (pattern has no slash) vs the full relpath. */
  matchBasename: boolean;
  /** Trailing-slash pattern: matches directories only. */
  dirOnly: boolean;
  /** Literal name (no `*`): also matched against any path segment (prune at any depth). */
  literal: boolean;
}

/** Compile one ignore pattern (a pragmatic subset of gitignore) to a matcher. */
function patternToRegExp(pattern: string): CompiledPattern {
  let p = pattern.trim();
  const dirOnly = p.endsWith('/');
  if (dirOnly) p = p.slice(0, -1);
  // A leading `**/` matches zero or more leading directories (so it matches at root too).
  let prefixAnySegments = false;
  if (p.startsWith('**/')) {
    prefixAnySegments = true;
    p = p.slice(3);
  }
  const matchBasename = !p.includes('/');
  const literal = !p.includes('*');
  const body = p
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*\*/g, '.*')
    .replace(/\*/g, '[^/]*');
  const source = prefixAnySegments ? `(?:.*/)?${body}` : body;
  return { re: new RegExp(`^${source}$`), matchBasename, dirOnly, literal };
}

export interface IgnoreMatcher {
  (relPath: string, isDir: boolean): boolean;
}

function loadFile(root: string, name: string): string[] {
  const path = join(root, name);
  if (!existsSync(path)) return [];
  try {
    return (
      readFileSync(path, 'utf8')
        .split(/\r?\n/)
        .map((l) => l.trim())
        // Skip blanks, comments, and negations (`!` re-includes are not supported).
        .filter((l) => l.length > 0 && !l.startsWith('#') && !l.startsWith('!'))
    );
  } catch {
    return [];
  }
}

/** Build an ignore matcher from defaults + the repo's .gitignore and .futurexignore. */
export function createIgnore(root: string): IgnoreMatcher {
  const patterns = [...DEFAULT_IGNORES, ...loadFile(root, '.gitignore'), ...loadFile(root, '.futurexignore')];
  const compiled = patterns.map(patternToRegExp);
  return (relPath: string, isDir: boolean): boolean => {
    const basename = relPath.split('/').pop() ?? relPath;
    for (const { re, matchBasename, dirOnly, literal } of compiled) {
      if (dirOnly && !isDir) continue; // `build/` matches directories only
      if (re.test(matchBasename ? basename : relPath)) return true;
      // Prune literal names (node_modules, dist, …) at any depth; do NOT do this
      // for glob patterns like `*.png`, which would wrongly prune a dir `x.png/`.
      if (matchBasename && literal && relPath.split('/').some((seg) => re.test(seg))) return true;
    }
    return false;
  };
}
